/**
 * MapBreadcrumb — "Manuale › Capitolo › Concetto", always visible above the
 * drill-down (task doc Core v1: "breadcrumb sempre visibile ... con ritorno
 * rapido ai livelli superiori"). Mono/eyebrow register — this is a wayfinding
 * instrument, not content, matching CourseLayout's own "Corso" eyebrow and
 * the DS's serif/sans/mono tripartition (mono = technical/positional labels).
 *
 * Purely presentational: ConceptMap.tsx owns the drill state and URL sync;
 * this component only renders it and calls back on click. Only reachable
 * (non-current) crumbs are clickable — the current, deepest crumb is plain
 * text with `aria-current="page"`.
 */
import Text from '@arianna/design-system/Text'

export interface MapBreadcrumbProps {
  manualLabel: string
  chapterLabel: string | null
  conceptLabel: string | null
  onSelectManual: () => void
  onSelectChapter: () => void
}

const crumbButtonStyle = {
  font: 'inherit',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: 'var(--ink-3)',
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
} as const

export function MapBreadcrumb({
  manualLabel,
  chapterLabel,
  conceptLabel,
  onSelectManual,
  onSelectChapter,
}: MapBreadcrumbProps) {
  return (
    <nav
      aria-label="Percorso nella mappa concettuale"
      data-testid="concept-map-breadcrumb"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-3) var(--space-5)',
        borderBottom: '1px solid var(--line)',
        flexShrink: 0,
        flexWrap: 'wrap',
      }}
    >
      {chapterLabel == null ? (
        <Text
          variant="mono-xs"
          as="span"
          aria-current="page"
          data-testid="breadcrumb-manual"
          style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
        >
          {manualLabel}
        </Text>
      ) : (
        <Text
          variant="mono-xs"
          as="button"
          onClick={onSelectManual}
          data-testid="breadcrumb-manual"
          style={{ ...crumbButtonStyle, textTransform: 'uppercase', letterSpacing: '0.08em' }}
        >
          {manualLabel}
        </Text>
      )}

      {chapterLabel != null && (
        <>
          <Text variant="mono-xs" as="span" aria-hidden="true" style={{ color: 'var(--ink-4)' }}>
            ›
          </Text>
          {conceptLabel == null ? (
            <Text
              variant="mono-xs"
              as="span"
              aria-current="page"
              data-testid="breadcrumb-chapter"
              style={{ color: 'var(--ink)' }}
            >
              {chapterLabel}
            </Text>
          ) : (
            <Text
              variant="mono-xs"
              as="button"
              onClick={onSelectChapter}
              data-testid="breadcrumb-chapter"
              style={crumbButtonStyle}
            >
              {chapterLabel}
            </Text>
          )}
        </>
      )}

      {conceptLabel != null && (
        <>
          <Text variant="mono-xs" as="span" aria-hidden="true" style={{ color: 'var(--ink-4)' }}>
            ›
          </Text>
          <Text
            variant="mono-xs"
            as="span"
            aria-current="page"
            data-testid="breadcrumb-concept"
            style={{ color: 'var(--ink)' }}
          >
            {conceptLabel}
          </Text>
        </>
      )}
    </nav>
  )
}
